import React, { Component as ReactComponent } from 'react';
import throttle from 'lodash/throttle';

/**
 * Viewport-aware higher order component.
 *
 * Provides the wrapped component with a `viewport` prop shaped
 * `{ width, height }`. Only Topbar uses it, to decide whether the mobile
 * menu can be open.
 *
 * The template this came from also exported `withTogglePageClassNames`,
 * `withDimensions` and `lazyLoadWithDimensions` — roughly 280 lines of
 * element-measuring and image lazy-loading machinery for marketplace
 * listing grids. Nothing referenced any of them.
 *
 * Note the initial `{ width: 0, height: 0 }`: dimensions are only known
 * after mount, so the first render always reports zero and the component
 * re-renders once immediately. Anything branching on `viewport.width` must
 * therefore tolerate 0 meaning "not measured yet", not "very narrow".
 */
export const withViewport = Component => {
  // Resize fires continuously while dragging, and each one re-renders.
  const WAIT_MS = 100;

  class WithViewportComponent extends ReactComponent {
    constructor(props) {
      super(props);
      this.state = { width: 0, height: 0 };
      this.handleWindowResize = this.handleWindowResize.bind(this);
      this.setViewport = throttle(this.setViewport.bind(this), WAIT_MS);
    }
    componentDidMount() {
      this.setViewport();
      window.addEventListener('resize', this.handleWindowResize);
      window.addEventListener('orientationchange', this.handleWindowResize);
    }
    componentWillUnmount() {
      window.removeEventListener('resize', this.handleWindowResize);
      window.removeEventListener('orientationchange', this.handleWindowResize);
    }
    handleWindowResize() {
      this.setViewport();
    }
    setViewport() {
      this.setState({ width: window.innerWidth, height: window.innerHeight });
    }
    render() {
      return <Component {...this.props} viewport={this.state} />;
    }
  }

  WithViewportComponent.displayName = `withViewport(${Component.displayName ||
    Component.name})`;

  return WithViewportComponent;
};
